import {Formik} from 'formik';
import {Alert, Stack, Text, TextInput, Title} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';
import {FormWithButtons} from '@flexinet/ui-components';
import {Application} from '../types';

// FNP-998: repair an application's provisioning-resource POINTERS.
//
// Owner requirement (2026-08-16): "I, as admin, should be able to go to the
// console application, fill in any missing information about source repository
// or infrastructure repository names and so on."
//
// WHY THIS EXISTS: these fields record WHICH resources an application owns.
// They are written by the provisioning orchestrator, but an application
// provisioned before a given field existed simply has no value — and the
// deprovision flow REFUSES to run without a persisted `infraStackNames`,
// because deriving a delete target is unsafe (an unfindable stack is reported
// as DELETE_COMPLETE, so a wrong name reads as a successful deletion).
//
// Every application provisioned before FNP-998 phase 1 is in that state,
// including canary-fnp711. Without this form the only fix would be writing to
// DynamoDB in prod, which the platform rules exclude.
//
// FNP-1066 adds `deploymentBucketNames` on the same footing (the field itself
// was added to the API by FNP-1015; this is the missing console half). It fails
// WORSE than a missing stack name rather than better: the teardown's
// EmptyAppBucket step treats an unrecorded bucket as non-fatal and skips, so
// the run looks healthy right up to DeleteInfraStack, which then hits a
// non-empty bucket and strands the stack in DELETE_FAILED. Both prod
// applications queued for teardown (canary-fnp711, flexinet-devops) hold 49
// objects apiece with no bucket name recorded.
//
// ⚠️ THIS IS NOT A RENAME FORM. `Application.name` is immutable (FNP-176)
// because it is the foreign-key shadow for these very resources; the API
// rejects any name change even from an admin. This edits the pointers, not the
// identity.

export interface ProvisioningMetadataValues {
    sourceRepositoryName: string;
    infraRepositoryName: string;
    sourcePipelineId: string;
    infraPipelineId: string;
    infraStackNameDev: string;
    infraStackNameTest: string;
    infraStackNameProd: string;
    deploymentBucketNameDev: string;
    deploymentBucketNameTest: string;
    deploymentBucketNameProd: string;
}

interface Props {
    record: Application;
    handleSubmit: (values: Record<string, unknown>) => void;
    handleCancel: () => void;
}

export const ProvisioningMetadataForm = (
    {
        record,
        handleSubmit,
        handleCancel,
    }: Props) => {

    const initialValues: ProvisioningMetadataValues = {
        sourceRepositoryName: (record.sourceRepositoryName as string) ?? '',
        infraRepositoryName: (record.infraRepositoryName as string) ?? '',
        sourcePipelineId: (record.sourcePipelineId as string) ?? '',
        infraPipelineId: (record.infraPipelineId as string) ?? '',
        infraStackNameDev: record.infraStackNames?.dev ?? '',
        infraStackNameTest: record.infraStackNames?.test ?? '',
        infraStackNameProd: record.infraStackNames?.prod ?? '',
        deploymentBucketNameDev: record.deploymentBucketNames?.dev ?? '',
        deploymentBucketNameTest: record.deploymentBucketNames?.test ?? '',
        deploymentBucketNameProd: record.deploymentBucketNames?.prod ?? '',
    };

    const onSubmit = (values: ProvisioningMetadataValues) => {
        const {
            infraStackNameDev,
            infraStackNameTest,
            infraStackNameProd,
            deploymentBucketNameDev,
            deploymentBucketNameTest,
            deploymentBucketNameProd,
            ...rest
        } = values;

        handleSubmit({
                         ...rest,
                         // ⚠️ ALL THREE KEYS ALWAYS. StageStringMap declares
                         // dev/test/prod as non-null, so a partial map makes
                         // AppSync null the WHOLE object on read —
                         // indistinguishable from never having been written,
                         // which is the very state this form repairs.
                         infraStackNames: {
                             dev: infraStackNameDev,
                             test: infraStackNameTest,
                             prod: infraStackNameProd,
                         },
                         // FNP-1066: same all-three-keys rule. The form is the
                         // only place that can guarantee it — the service-layer
                         // encoder passes through whatever keys it is given
                         // rather than padding, deliberately, so that an absent
                         // environment is never filled in with an empty name.
                         deploymentBucketNames: {
                             dev: deploymentBucketNameDev,
                             test: deploymentBucketNameTest,
                             prod: deploymentBucketNameProd,
                         },
                     });
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {(props) => (
                <FormWithButtons
                    {...props}
                    handleCancel={handleCancel}
                >
                    <Stack gap='lg'>
                        <Alert
                            variant='light'
                            color='yellow'
                            icon={<AiOutlineWarning/>}
                            title='Resource pointers, not the application name'
                        >
                            <Text size='sm'>
                                These fields record which AWS resources this application owns.
                                Correcting them does not move or rename anything — it tells the
                                platform where the existing resources are. The application name
                                itself cannot be changed.
                            </Text>
                        </Alert>

                        <div>
                            <Title order={5} mb='xs'>Repositories</Title>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Source repository'
                                    description='CodeCommit repository holding the application source'
                                    placeholder='my-app'
                                    name='sourceRepositoryName'
                                    value={props.values.sourceRepositoryName}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Infrastructure repository'
                                    description='CodeCommit repository holding the CDK infrastructure'
                                    placeholder='my-app-infra'
                                    name='infraRepositoryName'
                                    value={props.values.infraRepositoryName}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Pipelines</Title>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Source pipeline id'
                                    name='sourcePipelineId'
                                    value={props.values.sourcePipelineId}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Infrastructure pipeline id'
                                    name='infraPipelineId'
                                    value={props.values.infraPipelineId}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Deployed infrastructure stacks</Title>
                            <Text size='sm' c='dimmed' mb='sm'>
                                The CloudFormation stack that serves this application in each
                                environment, named <code>&lt;app&gt;-infra-&lt;stage&gt;</code>.
                                Decommissioning cannot run without the stack name for the
                                environment being torn down — it will not guess one, because a
                                stack it cannot find is indistinguishable from one already
                                deleted.
                            </Text>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Dev'
                                    placeholder='my-app-infra-dev'
                                    name='infraStackNameDev'
                                    value={props.values.infraStackNameDev}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Test'
                                    placeholder='my-app-infra-test'
                                    name='infraStackNameTest'
                                    value={props.values.infraStackNameTest}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Prod'
                                    placeholder='my-app-infra-prod'
                                    name='infraStackNameProd'
                                    value={props.values.infraStackNameProd}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>

                        <div>
                            <Title order={5} mb='xs'>Deployment buckets</Title>
                            <Text size='sm' c='dimmed' mb='sm'>
                                The S3 bucket holding this application&apos;s deployed content in
                                each environment, named{' '}
                                <code>&lt;app&gt;-infra-&lt;accountId&gt;-ui</code>. Decommissioning
                                empties this bucket before deleting the stack, and cannot do so if
                                the name is not recorded here — CloudFormation then refuses to
                                delete a non-empty bucket and the stack is left in DELETE_FAILED.
                                The platform will not derive or guess the name, for the same reason
                                it will not guess a stack name: a derived name that misses empties
                                nothing while reporting success.
                            </Text>
                            <Stack gap='sm'>
                                <TextInput
                                    label='Dev'
                                    placeholder='my-app-infra-111122223333-ui'
                                    name='deploymentBucketNameDev'
                                    value={props.values.deploymentBucketNameDev}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Test'
                                    placeholder='my-app-infra-444455556666-ui'
                                    name='deploymentBucketNameTest'
                                    value={props.values.deploymentBucketNameTest}
                                    onChange={props.handleChange}
                                />
                                <TextInput
                                    label='Prod'
                                    placeholder='canary-fnp711-infra-477488528677-ui'
                                    name='deploymentBucketNameProd'
                                    value={props.values.deploymentBucketNameProd}
                                    onChange={props.handleChange}
                                />
                            </Stack>
                        </div>
                    </Stack>
                </FormWithButtons>
            )}
        </Formik>
    );
};
